import React, { useMemo, useRef, useState, useEffect } from 'react';
import { exercises } from './exerciseData';
import { ExerciseCardWithDynamicThumbnail } from './VideoThumbnailGenerator';

// --- Voice Visualizer Component ---
const VoiceVisualizer = ({ isListening, isTranscribing, volume }) => {
  if (!isListening && !isTranscribing) return null;

  // Different styles for Listening vs Processing
  const bubbleScale = isListening ? 1 + volume * 0.7 : 1;
  const bubbleColor = isListening ? 'rgba(255, 153, 51, 0.8)' : 'rgba(19, 136, 8, 0.8)'; // Orange for mic, Green for processing
  const text = isListening ? "Listening..." : "Thinking...";
  const animation = isListening ? 'none' : 'pulse-ring 1.5s infinite';

  return (
    <div style={styles.visualizerOverlay}>
      <div style={styles.visualizerBubbleContainer}>
        <div style={{ 
            ...styles.visualizerBubble, 
            transform: `scale(${bubbleScale})`,
            backgroundColor: bubbleColor,
            animation: animation
        }}></div>
        <div style={styles.visualizerText}>{text}</div>
      </div>
    </div>
  );
};

// --- SVG Mic Icon ---
const MicIcon = ({ isListening }) => (
  <svg 
    width="24" 
    height="24" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke={isListening ? "#ffffff" : "#138808"} 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
    <line x1="12" y1="19" x2="12" y2="23" />
    <line x1="8" y1="23" x2="16" y2="23" />
  </svg>
);

const TakeawayGym = () => {
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [query, setQuery] = useState('');
  
  // --- Voice Search State ---
  const [isListening, setIsListening] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [volumeLevel, setVolumeLevel] = useState(0);

  // --- Refs for Audio Handling ---
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioContextRef = useRef(null);
  const streamRef = useRef(null);
  const animationFrameRef = useRef(null);
  const silenceTimerRef = useRef(null);

  // Backend URL
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:4000";

  // Cleanup function to stop mic, animations, and timers
  const cleanUpAudio = () => {
    if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') audioContextRef.current.close();
    if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
    
    if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
    }
    setVolumeLevel(0);
  };

  useEffect(() => {
    return () => cleanUpAudio();
  }, []);

  // Visualizer Logic: Calculates volume from stream
  const visualizeAudio = (stream) => {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    audioContextRef.current = audioContext;
    const source = audioContext.createMediaStreamSource(stream);
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 256;
    source.connect(analyser);
    const dataArray = new Uint8Array(analyser.frequencyBinCount);
    
    const draw = () => {
      animationFrameRef.current = requestAnimationFrame(draw);
      analyser.getByteFrequencyData(dataArray);
      const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
      setVolumeLevel(Math.min(avg / 128, 1.0));
    };
    draw();
  };

  // --- MAIN HANDLER: Start/Stop Recording ---
  const handleVoiceSearch = async () => {
    // 1. STOP RECORDING (Manual Click)
    if (isListening) {
      if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
      
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
        setIsListening(false);
      }
    } 
    // 2. START RECORDING
    else {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        streamRef.current = stream;
        visualizeAudio(stream);

        const mediaRecorder = new MediaRecorder(stream);
        mediaRecorderRef.current = mediaRecorder;
        audioChunksRef.current = [];

        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            audioChunksRef.current.push(event.data);
          }
        };

        // When recording stops, send to backend
        mediaRecorder.onstop = async () => {
            cleanUpAudio();
            
            setIsTranscribing(true);

            const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
            const formData = new FormData();
            formData.append('audio', audioBlob, 'voice-search.webm');

            try {
                const response = await fetch(`${backendUrl}/api/transcribe`, {
                    method: 'POST',
                    body: formData,
                });

                if (!response.ok) throw new Error("Transcription failed");
                
                const data = await response.json();
                if (data.text) {
                    setQuery(data.text.trim());
                }
            } catch (error) {
                console.error("Transcription Error:", error);
            } finally {
                setIsTranscribing(false);
            }
        };

        mediaRecorder.start();
        setIsListening(true);

        // AUTO-STOP TIMER (5 Seconds)
        silenceTimerRef.current = setTimeout(() => {
            if (mediaRecorder.state === "recording") {
                console.log("Auto-stopping recording...");
                mediaRecorder.stop();
                setIsListening(false);
            }
        }, 5000);

      } catch (err) {
        console.error("Mic access denied:", err);
        alert("Microphone access is required for voice search. Please enable it in your browser settings.");
      }
    }
  };

  // --- FEATURE: Videos of the Day Logic ---
  const videosOfTheDay = useMemo(() => {
    const today = new Date();
    const seed = today.getFullYear() * 10000 + (today.getMonth() + 1) * 100 + today.getDate();
    let s = seed;
    const random = () => {
      s = (s * 9301 + 49297) % 233280; 
      return s / 233280;
    };
    const shuffled = [...exercises];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled.slice(0, 5);
  }, []);

  const healthCategories = [
    { title: "A Daily Dose of Joy", subtitle: "Wellness exercises to brighten your day", tags: ["wellness", "mood", "gentle", "daily"], color: "#FF9933" },
    { title: "Your Next Summit - Dream it, Achieve it", subtitle: "Goal-oriented exercises for your aspirations", tags: ["goals", "strength", "progressive", "achievement"], color: "#138808" },
    { title: "Stay Strong, Stay Safe", subtitle: "Injury prevention and protective exercises", tags: ["prevention", "safety", "stability", "strength"], color: "#000080" },
    { title: "Find Your Relief", subtitle: "Pain management and comfort exercises", tags: ["pain", "relief", "therapeutic", "comfort"], color: "#9C27B0" },
    { title: "Breath, Beat, Balance", subtitle: "Heart, lung, and neurological wellness", tags: ["cardio", "breathing", "balance", "neurological"], color: "#FF5722" },
    { title: "Your Comeback Story", subtitle: "Post-operation recovery exercises", tags: ["recovery", "rehabilitation", "post-surgery", "healing"], color: "#795548" },
    { title: "The Balance Club", subtitle: "Fall prevention and stability training", tags: ["balance", "falls", "stability", "coordination"], color: "#607D8B" }
  ];

  const enhancedExercises = useMemo(() => {
    return exercises.map(exercise => {
      const assignedTags = [];
      const text = `${exercise.title} ${exercise.description}`.toLowerCase();
      if (text.includes('gentle') || text.includes('seated') || text.includes('comfort') || text.includes('yoga') || text.includes('relaxing')) assignedTags.push('wellness', 'mood', 'gentle', 'daily');
      if (text.includes('cardio') || text.includes('circuit') || text.includes('strength') || text.includes('build') || exercise.difficulty === 'Intermediate') assignedTags.push('goals', 'strength', 'progressive', 'achievement');
      if (text.includes('standing') || text.includes('stability') || text.includes('safe') || text.includes('fall') || text.includes('prevention')) assignedTags.push('prevention', 'safety', 'stability', 'strength');
      if (text.includes('stretch') || text.includes('nerve') || text.includes('relief') || text.includes('hamstring') || text.includes('neck')) assignedTags.push('pain', 'relief', 'therapeutic', 'comfort');
      if (text.includes('cardio') || text.includes('running') || text.includes('marching') || text.includes('exhale') || text.includes('inhale')) assignedTags.push('cardio', 'breathing', 'balance');
      if (text.includes('rehabilitation') || text.includes('recovery') || text.includes('therapeutic')) assignedTags.push('recovery', 'rehabilitation', 'healing');
      if (text.includes('balance') || text.includes('heel') || text.includes('toe') || text.includes('standing') || text.includes('coordination')) assignedTags.push('balance', 'falls', 'stability', 'coordination');
      return { ...exercise, tags: [...new Set(assignedTags)] };
    });
  }, []);

  const getExercisesByCategory = (categoryTags) => enhancedExercises.filter(exercise => exercise.tags.some(tag => categoryTags.includes(tag)));

  const normalizedQuery = query.trim().toLowerCase();

  const searchResults = useMemo(() => {
    if (!normalizedQuery || normalizedQuery.length < 3) return [];

    const queryWords = new Set(
      normalizedQuery
        .toLowerCase()
        .split(/\s+/)
        .filter(w => w.length >= 3)
    );

    if (queryWords.size === 0) return [];

    const exerciseScores = new Map();

    exercises.forEach(exercise => {
      let score = 0;
      const join = (arr) => (Array.isArray(arr) ? arr.join(' ') : (arr || '')).toLowerCase();
      const title = (exercise.title || '').toLowerCase();
      const description = (exercise.description || '').toLowerCase();
      const goals = join(exercise.goalIntent);
      const symptoms = join(exercise.symptomFeeling);
      const bodyParts = join(exercise.bodyPart);
      const activities = join(exercise.activityContext);
      const types = join(exercise.exerciseType);
      const category = join(exercise.category);
      const difficulty = join(exercise.difficultyIntensity);

      let matchCount = 0;
      const fullSearchText = `${title} ${goals} ${symptoms} ${bodyParts} ${activities} ${types} ${description} ${category} ${difficulty}`;

      queryWords.forEach(word => {
        if (!fullSearchText.includes(word)) return;
        matchCount++;
        if (title.includes(word)) score += 15;
        if (goals.includes(word)) score += 10;
        if (symptoms.includes(word)) score += 10;
        if (bodyParts.includes(word)) score += 7;
        if (activities.includes(word)) score += 5;
        if (types.includes(word)) score += 5;
        if (description.includes(word)) score += 3;
        if (category.includes(word)) score += 2;
        if (difficulty.includes(word)) score += 1;
      });

      if (matchCount > 1) score *= (1 + (matchCount / queryWords.size));
      if (score > 0) exerciseScores.set(exercise, score);
    });

    return Array.from(exerciseScores.entries())
      .sort((a, b) => b[1] - a[1])
      .map(entry => entry[0]);

  }, [normalizedQuery]);

  const openVideoModal = (exercise) => setSelectedVideo(exercise);
  const closeVideoModal = () => setSelectedVideo(null);
  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Beginner': return '#4CAF50';
      case 'Intermediate': return '#FF9800';
      case 'Advanced': return '#F44336';
      default: return '#9E9E9E';
    }
  };

  const getSearchInputPadding = () => {
    let paddingRight = 3.5; 
    paddingRight += 3.5; 
    if (query) paddingRight += 2.8;
    return { ...styles.searchInput, paddingRight: `${paddingRight}rem` };
  };

  return (
    <div style={styles.netflixContainer}>
      <VoiceVisualizer isListening={isListening} isTranscribing={isTranscribing} volume={volumeLevel} />
      
      <header style={styles.heroSection}>
        <div style={styles.heroContent}>
          <h1 style={styles.heroTitle}>V.E.D.A</h1>
          <p style={styles.heroSubtitle}>Video Exercise Discovery Assistant</p>
          <div style={styles.heroStats}>
            <span style={styles.stat}>{exercises.length} Videos</span>
            <span style={styles.stat}>7 Categories</span>
            <span style={styles.stat}>Expert Approved</span>
          </div>
        </div>
      </header>

      <section style={styles.searchSection}>
        <div style={styles.searchContainer}>
          <form onSubmit={(e) => e.preventDefault()} style={styles.searchBarWrapper} role="search" aria-label="Search exercises">
            <input 
              type="search" 
              placeholder="Describe your goal, e.g., 'prepare for a hike' or 'help with back pain'" 
              value={query} 
              onChange={(e) => setQuery(e.target.value)} 
              style={getSearchInputPadding()} 
            />
            
            {query && (
              <button 
                type="button" 
                onClick={() => setQuery('')}
                aria-label="Clear search" 
                className="clear-btn-hover"
                style={{ ...styles.clearBtn, right: '7.2rem' }}
              >
                ✕
              </button>
            )}

            {/* MIC BUTTON */}
            <button 
              type="button" 
              onClick={handleVoiceSearch} 
              aria-label="Search by voice" 
              className={isListening ? "listening-pulse" : ""}
              style={{ 
                ...styles.micBtn, 
                right: '3.8rem',
                backgroundColor: isListening ? '#F44336' : 'transparent',
                borderColor: isListening ? '#F44336' : 'transparent',
                transform: isListening ? 'translateY(-50%) scale(1.1)' : 'translateY(-50%)'
              }} 
              title="Search by voice"
            >
              <MicIcon isListening={isListening} />
            </button>

            <button type="submit" style={styles.searchBtn} aria-label="Search">🔍</button>
          </form>
        </div>
      </section>
      
      <main style={styles.rowsWrapper} className="content-section">
        
        {/* 1. Search Results (if active) */}
        {normalizedQuery && <SearchResultsRow query={normalizedQuery} results={searchResults} onPlay={openVideoModal} />}

        {/* 2. Videos of the Day (Only show if NOT searching) */}
        {!normalizedQuery && (
          <CategoryRow 
            category={{
              title: "Workout of the Day", 
              subtitle: "Fresh picks updated daily, just for you", 
              color: "#E91E63"
            }} 
            exercises={videosOfTheDay} 
            onPlay={openVideoModal} 
          />
        )}

        {/* 3. Standard Categories */}
        {healthCategories.map((category) => {
          const categoryExercises = getExercisesByCategory(category.tags);
          if (categoryExercises.length === 0) return null;
          return <CategoryRow key={category.title} category={category} exercises={categoryExercises} onPlay={openVideoModal} />;
        })}

      </main>
      
      {selectedVideo && <VideoModal video={selectedVideo} onClose={closeVideoModal} getDifficultyColor={getDifficultyColor} />}
    </div>
  );
};

const SearchResultsRow = ({ query, results, onPlay }) => {
  const scrollerRef = useRef(null);
  const scrollByCardWidths = (dir = 1) => {
    const node = scrollerRef.current;
    if (!node) return;
    const card = node.querySelector('.video-card');
    const width = card ? card.getBoundingClientRect().width : 280;
    node.scrollBy({ left: dir * width * 3, behavior: 'smooth' });
  };
  const hasQuery = query.length >= 3;
  return (
    <section style={styles.searchResultsSection}>
      <div style={styles.searchHeader}>
        <h2 style={styles.searchTitle}>{hasQuery ? `Recommended for you (${results.length})` : "Keep typing to see recommendations..."}</h2>
      </div>
      {hasQuery && results.length === 0 && (
        <div style={styles.noResults}>
          <p>We couldn't find specific exercises for your search.</p>
          <p style={styles.noResultsSubtext}>Try rephrasing, or browse the categories below.</p>
        </div>
      )}
      {hasQuery && results.length > 0 && (
        <>
          <div style={styles.sectionHeader}>
            <p style={styles.categorySubtitle}>Based on your search for: "{query}"</p>
            <div style={styles.rowControls}>
              <button style={styles.navPill} onClick={() => scrollByCardWidths(-1)} aria-label="Scroll left">◀</button>
              <button style={styles.navPill} onClick={() => scrollByCardWidths(1)} aria-label="Scroll right">▶</button>
            </div>
          </div>
          <div style={styles.rowScroller} ref={scrollerRef}>
            {results.map((exercise, idx) => <ExerciseCardWithDynamicThumbnail key={`search-${exercise.title}-${idx}`} exercise={exercise} onPlay={onPlay} thumbnailTimestamp={exercise.thumbnailTimestamp || 5} thumbnailSrc={exercise.thumbnailSrc} />)}
          </div>
        </>
      )}
    </section>
  );
};

const CategoryRow = ({ category, exercises, onPlay }) => {
  const scrollerRef = useRef(null);
  const scrollByCardWidths = (dir = 1) => {
    const node = scrollerRef.current;
    if (!node) return;
    const card = node.querySelector('.video-card');
    const width = card ? card.getBoundingClientRect().width : 280;
    node.scrollBy({ left: dir * width * 3, behavior: 'smooth' });
  };
  return (
    <section style={styles.categorySection}>
      <div style={styles.categoryHeader}>
        <div>
          <h2 style={{ ...styles.categoryTitle, color: category.color }}>{category.title}</h2>
          <p style={styles.categorySubtitle}>{category.subtitle}</p>
        </div>
        <div style={styles.rowControls}>
          <button style={{ ...styles.navPill, backgroundColor: category.color }} onClick={() => scrollByCardWidths(-1)} aria-label="Scroll left">◀</button>
          <button style={{ ...styles.navPill, backgroundColor: category.color }} onClick={() => scrollByCardWidths(1)} aria-label="Scroll right">▶</button>
        </div>
      </div>
      <div style={styles.rowScroller} ref={scrollerRef}>
        {exercises.map((exercise, idx) => <ExerciseCardWithDynamicThumbnail key={`${category.title}-${exercise.title}-${idx}`} exercise={exercise} onPlay={onPlay} categoryColor={category.color} thumbnailTimestamp={exercise.thumbnailTimestamp || 5} thumbnailSrc={exercise.thumbnailSrc} />)}
      </div>
    </section>
  );
};

// --- VideoModal: now fetches CloudFront signed URLs for video and audio ---
const VideoModal = ({ video, onClose, getDifficultyColor }) => {
  const [secureVideoUrl, setSecureVideoUrl] = useState(null);
  const [secureAudioUrl, setSecureAudioUrl] = useState(null);

  useEffect(() => {
    if (!video) return;
    const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:4000";

    // Fetch a signed CloudFront URL for the video
    if (video.videoSrc) {
      fetch(`${backendUrl}/api/signed-url/${encodeURIComponent(video.videoSrc)}`, {
        credentials: 'include',
      })
        .then((r) => r.json())
        .then((data) => setSecureVideoUrl(data.signedUrl))
        .catch((err) => console.error('Failed to get signed video URL:', err));
    }

    // Fetch a signed CloudFront URL for the audio guide (if present)
    if (video.audioSrc) {
      fetch(`${backendUrl}/api/signed-url/${encodeURIComponent(video.audioSrc)}`, {
        credentials: 'include',
      })
        .then((r) => r.json())
        .then((data) => setSecureAudioUrl(data.signedUrl))
        .catch((err) => console.error('Failed to get signed audio URL:', err));
    }
  }, [video]);

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <button style={styles.closeButton} onClick={onClose}>×</button>

        <div style={styles.modalVideoWrapper}>
          {!secureVideoUrl && <div style={styles.videoPlaceholder}>Loading...</div>}
          {secureVideoUrl && (
            <video
              key={secureVideoUrl}
              style={styles.modalVideo}
              src={secureVideoUrl}
              controls
              autoPlay
              playsInline           
              controlsList="nodownload"
              onContextMenu={(e) => e.preventDefault()}
            />
          )}
        </div>

        <div style={styles.modalInfo}>
          <h3 style={styles.modalTitle}>{video.title}</h3>
          
          {/* Audio Guide Player */}
          {secureAudioUrl && (
            <div style={styles.audioContainer}>
              <div style={styles.audioHeader}>
                <span style={{fontSize: '0.9rem', fontWeight: 600, color: '#138808'}}>🔊 Practice with Audio Guide</span>
              </div>
              <audio 
                controls 
                src={secureAudioUrl} 
                style={styles.audioPlayer}
                controlsList="nodownload"
              >
                Your browser does not support the audio element.
              </audio>
            </div>
          )}

          <div style={styles.modalMeta}>
            <span style={styles.modalDuration}>⏱ {video.duration}</span>
            <span style={{ ...styles.modalDifficulty, color: getDifficultyColor(video.difficulty) }}>
              {video.difficulty}
            </span>
          </div>

          <p style={styles.modalDescription}>{video.description}</p>

          {video.dose && (
            <div style={styles.doseContainer}>
              <strong style={styles.doseLabel}>Recommended Dose:</strong>
              <p style={{ margin: 0 }}>{video.dose}</p>
            </div>
          )}

          {video.script && (
            <div style={styles.scriptContainer}>
              <h4 style={styles.scriptTitle}>🗣 Voice Instructions</h4>
              <p style={styles.scriptText}>"{video.script}"</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


const styles = {
  visualizerOverlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 },
  visualizerBubbleContainer: { position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' },
  visualizerBubble: { width: '150px', height: '150px', borderRadius: '50%', transition: 'transform 0.1s ease-out, background-color 0.3s ease', border: '4px solid rgba(255, 255, 255, 0.5)', boxShadow: '0 0 30px rgba(255, 153, 51, 0.5)' },
  visualizerText: { position: 'absolute', color: 'white', fontSize: '1.2rem', fontWeight: 'bold', textShadow: '0 1px 3px rgba(0,0,0,0.5)', top: '180px' },
  netflixContainer: { width: '100%', height: '100vh', backgroundColor: '#ffffff', color: '#333333', fontFamily: 'Netflix Sans, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif', overflow: 'auto', position: 'relative' },
  heroSection: { background: 'linear-gradient(135deg, #FF9933 0%, #ffffff 50%, #138808 100%)', padding: '2rem 2rem 2rem', textAlign: 'center', position: 'relative', overflow: 'hidden', borderBottom: '4px solid #000080', marginBottom: '0' },
  heroContent: { maxWidth: '900px', margin: '0 auto', position: 'relative', zIndex: 2 },
  heroTitle: { fontSize: 'clamp(2.5rem, 5vw, 4rem)', fontWeight: 700, marginBottom: '0.5rem', background: 'linear-gradient(45deg, #FF9933, #000080)', backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', textShadow: '0 2px 4px rgba(0,0,0,0.1)' },
  heroSubtitle: { fontSize: '1.15rem', lineHeight: 1.6, marginBottom: '1.25rem', color: '#333333', maxWidth: '700px', marginInline: 'auto', fontWeight: 500 },
  heroStats: { display: 'flex', justifyContent: 'center', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.25rem' },
  stat: { fontSize: '0.95rem', color: '#ffffff', fontWeight: 600, border: '2px solid #FF9933', padding: '0.4rem 0.9rem', borderRadius: 20, backgroundColor: '#FF9933', boxShadow: '0 2px 8px rgba(255, 153, 51, 0.3)' },
  searchSection: { backgroundColor: '#f8f9fa', borderBottom: '3px solid #138808', padding: '2rem 1.25rem', position: 'sticky', top: 0, zIndex: 100 },
  searchContainer: { maxWidth: '900px', margin: '0 auto' },
  searchBarWrapper: { display: 'flex', alignItems: 'center', position: 'relative', width: '100%' },
  searchInput: { width: '100%', fontSize: '1.2rem', padding: '1rem 3.25rem 1rem 1.25rem', borderRadius: '999px', border: '2px solid #138808', outline: 'none', boxShadow: '0 3px 10px rgba(19, 136, 8, 0.15)', backgroundColor: '#ffffff', transition: 'padding-right 0.3s ease' },
  searchBtn: { position: 'absolute', right: '0.6rem', top: '50%', transform: 'translateY(-50%)', background: '#138808', color: '#fff', border: '2px solid #0d5c06', borderRadius: '999px', padding: '0.55rem 0.9rem', cursor: 'pointer', fontSize: '1.1rem' },
  clearBtn: { position: 'absolute', top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', color: '#999', fontSize: '1.2rem', cursor: 'pointer', padding: '8px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', width: '35px', height: '35px' },
  micBtn: { 
    position: 'absolute', 
    top: '50%', 
    transform: 'translateY(-50%)', 
    background: 'transparent', 
    border: '2px solid transparent', 
    cursor: 'pointer', 
    padding: '8px', 
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
    width: '40px',
    height: '40px'
  },
  rowsWrapper: { padding: '2rem 1.25rem 3rem' },
  searchResultsSection: { marginBottom: '3rem', padding: '2rem', backgroundColor: '#ffffff', borderRadius: '12px', border: '2px solid #138808', boxShadow: '0 4px 12px rgba(19, 136, 8, 0.1)' },
  searchHeader: { marginBottom: '1.5rem' },
  searchTitle: { fontSize: '1.6rem', fontWeight: 700, color: '#138808', margin: 0 },
  noResults: { textAlign: 'center', padding: '2rem', color: '#666' },
  noResultsSubtext: { fontSize: '0.9rem', color: '#999', marginTop: '0.5rem' },
  categorySection: { marginBottom: '3rem' },
  categoryHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '1rem', marginBottom: '1rem' },
  audioContainer: {
    backgroundColor: '#f0f7f0',
    padding: '1rem',
    borderRadius: '8px',
    marginBottom: '1.5rem',
    border: '1px solid #c8e6c9',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem'
  },
  audioHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '4px'
  },
  audioPlayer: {
    width: '100%',
    height: '40px',
  },
  categoryTitle: { fontSize: '1.8rem', fontWeight: 700, margin: 0, marginBottom: '0.25rem' },
  categorySubtitle: { fontSize: '1rem', color: '#666', margin: 0, fontWeight: 400 },
  sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', marginBottom: '0.75rem', flexWrap: 'wrap' },
  rowControls: { display: 'flex', gap: '0.5rem' },
  navPill: { background: '#138808', color: '#fff', border: 'none', padding: '0.4rem 0.7rem', borderRadius: 999, cursor: 'pointer', fontSize: '1rem', boxShadow: '0 2px 6px rgba(19,136,8,.25)' },
  rowScroller: { display: 'grid', gridAutoFlow: 'column', gridAutoColumns: '260px', gap: '1rem', overflowX: 'auto', overscrollBehaviorX: 'contain', scrollSnapType: 'x proximity', paddingBottom: '0.25rem' },
  modalOverlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '2rem' },
  modalContent: { backgroundColor: '#ffffff', borderRadius: '12px', maxWidth: '900px', width: '100%', maxHeight: '90vh', overflow: 'auto', position: 'relative', border: '3px solid #000080' },
  closeButton: { position: 'absolute', top: '1rem', right: '1rem', backgroundColor: '#FF9933', color: '#ffffff', border: 'none', width: 40, height: 40, borderRadius: '50%', fontSize: '1.5rem', cursor: 'pointer', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background-color 0.3s ease', boxShadow: '0 2px 8px rgba(255, 153, 51, 0.4)' },
  modalVideoWrapper: { width: '100%', aspectRatio: '16 / 9', background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modalVideo: { width: '100%', height: '100%', border: 'none', objectFit: 'contain' },
  videoPlaceholder: { display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ffffff', fontSize: '1.2rem', fontWeight: 600, height: '100%' },
  modalInfo: { padding: '1.25rem 1.5rem 2rem' },
  modalTitle: { fontSize: '1.6rem', fontWeight: 700, marginBottom: '0.5rem', color: '#000080' },
  modalMeta: { display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem', flexWrap: 'wrap' },
  modalDuration: { fontSize: '1rem', color: '#666666' },
  modalDifficulty: { fontSize: '1rem', fontWeight: 600 },
  modalDescription: { fontSize: '1.05rem', lineHeight: 1.6, color: '#333333', marginBottom: '1rem' },
  doseContainer: { backgroundColor: 'rgba(19, 136, 8, 0.1)', borderLeft: '4px solid #138808', padding: '1rem', borderRadius: '4px', marginBottom: '1.5rem', color: '#333' },
  doseLabel: { display: 'block', color: '#138808', marginBottom: '0.25rem', fontSize: '0.95rem' },
  scriptContainer: { backgroundColor: '#f8f9fa', padding: '1.25rem', borderRadius: '12px', marginTop: '1rem', border: '1px solid #e9ecef' },
  scriptTitle: { fontSize: '1rem', fontWeight: 700, color: '#000080', marginTop: 0, marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' },
  scriptText: { fontSize: '1.05rem', lineHeight: 1.6, color: '#555', fontStyle: 'italic', margin: 0 },
};

const addHoverEffects = () => {
  const styleId = 'enhanced-takeaway-gym-hover-styles';
  if (document.getElementById(styleId)) return;
  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    .video-card:hover { transform: scale(1.04); border-color: #FF9933; box-shadow: 0 8px 25px rgba(255, 153, 51, 0.2); }
    .video-card:hover .thumbnail { transform: scale(1.06); }
    .video-card:hover .play-overlay { opacity: 1; }
    .video-card:hover .play-button { transform: scale(1); background-color: #138808; }
    .close-button:hover { background-color: #138808 !important; }
    .clear-btn-hover:hover { color: #F44336 !important; background-color: rgba(244, 67, 54, 0.1) !important; }
    @keyframes pulse-ring {
      0% { box-shadow: 0 0 0 0 rgba(19, 136, 8, 0.7); }
      70% { box-shadow: 0 0 0 10px rgba(19, 136, 8, 0); }
      100% { box-shadow: 0 0 0 0 rgba(19, 136, 8, 0); }
    }
    .listening-pulse { animation: pulse-ring 1.5s infinite; }
    input[type="search"]::-webkit-search-decoration,
    input[type="search"]::-webkit-search-cancel-button,
    input[type="search"]::-webkit-search-results-button,
    input[type="search"]::-webkit-search-results-decoration { display: none; }
  `;
  document.head.appendChild(style);
};
if (typeof document !== 'undefined') addHoverEffects();

export default TakeawayGym;